import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Store,
  Users,
  Database,
  TrendingDown,
  Settings,
  FileText,
  X,
  LogOut,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import ThemeToggle from './ThemeToggle';

const menuItems = [
  { path: '/outlets', icon: Store, label: 'Outlets' },
  { path: '/users', icon: Users, label: 'Users & Roles' },
  { path: '/masters', icon: Database, label: 'Global Masters' },
  { path: '/vendor-analytics', icon: TrendingDown, label: 'Vendor Analytics' },
  { path: '/configuration', icon: Settings, label: 'Configuration' },
  { path: '/audit-logs', icon: FileText, label: 'Audit Logs' },
];

const MobileMenu = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const { logout } = useAuth();
  const currentColors = colors[theme];
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    onClose();
    logout();
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 md:hidden"
            onClick={onClose}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed right-0 top-0 bottom-0 w-80 z-50 md:hidden"
            style={
              {
                background: currentColors.background,
                boxShadow: `-4px 0 20px ${currentColors.shadowDark}`,
              }
            }
          >
            {/* Header */}
            <div className="p-6 flex items-center justify-between border-b" style={{ borderColor: currentColors.border }}>
              <h2 className="text-xl font-bold" style={{ color: currentColors.textPrimary }}>
                Menu
              </h2>
              <motion.button
                onClick={onClose}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={
                  {
                    background: currentColors.cardBg,
                    boxShadow: getNeumorphicShadow(theme),
                  }
                }
                whileTap={{ scale: 0.95 }}
              >
                <X size={20} style={{ color: currentColors.textSecondary }} />
              </motion.button>
            </div>

            {/* Navigation Items */}
            <nav className="p-4 space-y-2">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  style={{ textDecoration: 'none' }}
                >
                  {({ isActive }) => (
                    <motion.div
                      className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={
                        {
                          background: isActive ? currentColors.cardBg : 'transparent',
                          boxShadow: isActive ? getNeumorphicShadow(theme) : 'none',
                          color: isActive ? currentColors.accentBlue : currentColors.textPrimary,
                        }
                      }
                      whileTap={{ scale: 0.98 }}
                    >
                      <item.icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </motion.div>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Footer with Theme Toggle and Logout */}
            <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3 border-t" style={{ borderColor: currentColors.border }}>
              {/* Theme Toggle */}
              <div className="flex justify-center">
                <ThemeToggle />
              </div>

              {/* Logout Button */}
              <motion.button
                onClick={handleLogoutClick}
                className="w-full px-4 py-3 rounded-xl flex items-center justify-center gap-3 font-medium"
                style={{
                  background: currentColors.cardBg,
                  boxShadow: getNeumorphicShadow(theme),
                  color: currentColors.accentRed,
                }}
                whileTap={{ scale: 0.98 }}
              >
                <LogOut size={20} />
                <span>Logout</span>
              </motion.button>

              {/* Version */}
              <div className="text-center text-xs" style={{ color: currentColors.textSecondary }}>
                v1.1 - Multi-Tenant
              </div>
            </div>
          </motion.div>

          {/* Logout Confirmation Modal */}
          {showLogoutConfirm && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 px-4">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full"
              >
                <h3 className="text-xl font-bold mb-2" style={{ color: currentColors.textPrimary }}>
                  Logout from Control Room?
                </h3>
                <p className="text-sm mb-6" style={{ color: currentColors.textSecondary }}>
                  Are you sure you want to logout? You'll need to sign in again to access your dashboard.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelLogout}
                    className="flex-1 px-4 py-2 rounded-lg font-medium border"
                    style={{
                      background: currentColors.cardBg,
                      borderColor: currentColors.border,
                      color: currentColors.textPrimary,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmLogout}
                    className="flex-1 px-4 py-2 rounded-lg font-medium text-white"
                    style={{
                      background: currentColors.accentRed,
                    }}
                  >
                    Logout
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;
